import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import TopNavbar from "./TopNavbar";
import {
  FaHome,
  FaTasks,
  FaCoins,
  FaWallet,
  FaSignInAlt,
  FaSignOutAlt,
} from "react-icons/fa";

import Mainpage from "./mainpage";
import Wallet from "./wallet";
import TasksSection from "../task/TasksSection";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import Alert from "./Alert";
import InvestPlanCards from "./InvestPlanCards";
import WinnerAnnouncementModal from "./WinnerAnnouncementModal";

export default function Dashboard() {
  const [activeNav, setActiveNav] = useState("home");
  const [currentPage, setCurrentPage] = useState("home");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { alert, showAlert, HandleFetchUserData } = useAppContext();
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutUser, checkAuth } = useAuth();
  const isPlansPage = location.pathname === "/plans";

  useEffect(() => {
    HandleFetchUserData();
  }, [HandleFetchUserData]);

  useEffect(() => {
    const init = async () => {
      const res = await checkAuth();
      setIsLoggedIn(!!res?.success);
    };
    init();
  }, [checkAuth]);

  const handleProtectedNav = async (page) => {
    const res = await checkAuth();
    if (!res?.success) {
      navigate("/login");
      return;
    }
    setIsLoggedIn(true);
    setCurrentPage(page);
    setActiveNav(page);
  };

  const goToPage = async (page) => {
    if (isPlansPage) navigate("/");

    if (page === "home") {
      setCurrentPage("home");
      setActiveNav("home");
      return;
    }
    if (page === "tasks" || page === "wallet") {
      await handleProtectedNav(page);
      return;
    }
    setCurrentPage(page);
    setActiveNav(page);
  };

  const handleLogout = async () => {
    await logoutUser();
    setIsLoggedIn(false);
    setCurrentPage("home");
    setActiveNav("home");
    navigate("/login");
  };

  const navItems = [
    { icon: FaHome, label: "Home", page: "home", onClick: () => goToPage("home") },
    { icon: FaTasks, label: "Tasks", page: "tasks", onClick: () => goToPage("tasks") },
    // { icon: FaCoins, label: "Earn", page: "earn", big: true, onClick: () => goToPage("home") },
    { icon: FaWallet, label: "Wallet", page: "wallet", onClick: () => goToPage("wallet") },
    isLoggedIn
      ? { icon: FaSignOutAlt, label: "Logout", page: "logout", onClick: handleLogout }
      : { icon: FaSignInAlt, label: "Login", page: "login", onClick: () => navigate("/login") },
  ];

  return (
    <div className="min-h-screen bg-[#07111F] text-slate-900 font-sans flex flex-col">
      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />

      <TopNavbar />
      <WinnerAnnouncementModal autoCloseMs={5000} showOncePerSession />

      {/* ================= CONTENT AREA ================= */}
      {/* Added responsive bottom padding (pb-28 md:pb-32) to prevent the fixed bottom nav from covering content */}
      <main className="flex-1 pb-28 md:pb-32 w-full">
        {isPlansPage ? (
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
            <InvestPlanCards />
          </div>
        ) : (
          <div className="w-full max-w-7xl mx-auto">
            {currentPage === "home" && <Mainpage />}
            {currentPage === "tasks" && <TasksSection />}
            {currentPage === "wallet" && <Wallet />}
          </div>
        )}
      </main>

      {/* ================= PREMIUM BOTTOM NAV ================= */}
      {/* Pointer-events-none on wrapper ensures you can still click things on the left/right of the pill on desktop */}
      <div className="fixed bottom-4 sm:bottom-6 left-0 right-0 px-4 z-50 pointer-events-none">
        <motion.nav
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="pointer-events-auto w-full max-w-[22rem] sm:max-w-md md:max-w-lg mx-auto h-[4.5rem] sm:h-20 bg-[#0B1D34]/95 backdrop-blur-2xl border border-blue-800/40 shadow-[0_25px_60px_rgba(0,0,0,.6)] rounded-full flex items-center justify-around px-2 sm:px-4"
        >
          {navItems.map((item, idx) => {
            const isActive = activeNav === item.page;
            return (
              <motion.button
                key={idx}
                className="relative flex flex-col items-center justify-center w-14 h-14 sm:w-16 sm:h-16 transition-all"
                onClick={item.onClick}
                whileTap={{ scale: 0.9 }}
              >
                {/* Active Indicator Dot */}
                {isActive && !item.big && (
                  <motion.div 
                    layoutId="activeTab"
                    className="absolute -top-1 sm:-top-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full shadow-lg shadow-yellow-400/50"                  
                  />
                )}

                <item.icon
                  className={`${
                    item.big
                      ? "text-yellow-400 drop-shadow-lg text-3xl sm:text-4xl mb-1 sm:mb-2"
                      : isActive
                        ? "text-yellow-400 text-lg sm:text-xl"
                        : "text-slate-500 text-lg sm:text-xl"
                  } transition-colors duration-300`}
                />
                
                {!item.big && (
                  <span className={`mt-1 text-[9px] sm:text-[10px] font-black uppercase tracking-tighter ${
                    isActive ? "text-yellow-400" : "text-slate-500"                  
                  }`}>
                    {item.label}
                  </span>
                )}
                
                {/* Special styling for the BIG middle button (if uncommented) */}
                {item.big && (
                  <div className="absolute -top-6 sm:-top-8 bg-[#071523] p-1.5 sm:p-2 rounded-full shadow-xl border border-blue-800/40">
                    <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-2.5 sm:p-3 rounded-full text-[#07111F] shadow-yellow-500/40 shadow-xl">
                       <FaCoins className="text-lg sm:text-xl" />
                    </div>
                  </div>
                )}
              </motion.button>
            );
          })}
        </motion.nav>
      </div>
    </div>
  );
}